import { InMemoryCache, Reference, defaultDataIdFromObject, makeVar } from '@apollo/client';
import type { StoreObject } from '@apollo/client';
import type { FieldFunctionOptions, FieldPolicy } from '@apollo/client/cache';
import possibleTypes from '../types/possibleTypes.json';
import { getPendingCreatedIds, getPendingDeletedIds, getPendingMovedGroupId } from '../hooks/potree/annotationMutationCoalescer';

type KeySpecifier = ReadonlyArray<string>;

interface PaginationVariables {
  ignoreMerge?: boolean;
  page?: number;
  limit?: number;
  pagination?: { page?: number; limit?: number };
}

export const newMessageCountByConversationId = makeVar<{ [key: string]: number }>({});

/**
 * Cache-layer shape of Project.state: the entity lists hold normalized
 * References (annotations/groups/orderedIdentifiers key on `identifier` via
 * dataIdFromObject); everything else passes through untouched.
 */
interface StateStoreObject extends StoreObject {
  annotations?: Reference[];
  groups?: Reference[];
  orderedIdentifiers?: Reference[];
}

interface CursorPaginatedData {
  data: Reference[] | Record<string, Reference>;
  [key: string]: unknown;
}

const cursorLimitUniquePagination = (keyArgs: KeySpecifier | false = false): FieldPolicy<CursorPaginatedData> => {
  return {
    keyArgs,
    merge(existing, incoming, { readField, variables }: FieldFunctionOptions) {
      const vars = variables as PaginationVariables | undefined;
      const { ignoreMerge = false } = vars ?? {};
      if (ignoreMerge) return incoming;
      const merged: Record<string, Reference> = existing ? { ...(existing.data as Record<string, Reference>) } : {};
      const incomingData = (incoming.data || []) as Reference[];
      incomingData.forEach((item) => {
        const id = readField<string>('id', item);
        if (id) {
          merged[id] = item;
        }
      });
      return { ...incoming, data: merged };
    },
    read(existing) {
      return (
        existing && {
          ...existing,
          ...(existing.data && { data: Object.values(existing.data) }),
        }
      );
    },
  };
};

export const cache = new InMemoryCache({
  possibleTypes,
  typePolicies: {
    Query: {
      fields: {
        projectCommentsByObjectId: cursorLimitUniquePagination(['projectId', 'objectId']),
        superadminOrganisations: cursorLimitUniquePagination(['filters']),
      },
    },
    Project: {
      fields: {
        state: {
          // Shallow field merge PLUS clobber protection for the entity lists:
          // annotation/group creates are optimistic and their mutations can be
          // deferred (see annotationMutationCoalescer), so a full-state
          // response from an OVERLAPPING mutation reflects a server that
          // hasn't seen them yet. Replacing the arrays wholesale would make
          // just-created items vanish until their own response lands (the
          // "one-behind" panel) and just-deleted items resurrect. Entries with
          // a pending create survive the write; entries with a pending delete
          // are dropped from it.
          merge(existing: StateStoreObject | undefined, incoming: StateStoreObject, { mergeObjects, readField }: FieldFunctionOptions): StateStoreObject {
            if (!existing) return incoming;
            const merged = { ...(mergeObjects(existing, incoming) as StateStoreObject) };
            const created = getPendingCreatedIds();
            const deleted = getPendingDeletedIds();
            if (created.size === 0 && deleted.size === 0) return merged;

            for (const key of ['annotations', 'groups', 'orderedIdentifiers'] as const) {
              const incomingList = incoming[key];
              if (!incomingList) continue; // subset write not touching this list
              const idOf = (item: Reference): string | undefined => readField<string>('identifier', item);
              const incomingIds = new Set(incomingList.map(idOf));
              const kept = incomingList.filter((item) => {
                const id = idOf(item);
                return !id || !deleted.has(id);
              });
              const preserved = (existing[key] ?? []).filter((item) => {
                const id = idOf(item);
                return !!id && created.has(id) && !incomingIds.has(id);
              });
              merged[key] = [...kept, ...preserved];
            }
            return merged;
          },
        },
      },
    },
    Annotation: {
      fields: {
        groupIdentifier: {
          // Field-level clobber protection for optimistic moves: a concurrent
          // full-state response may rewrite this normalized field to the
          // pre-move value. While the move mutation is in flight, reads return
          // the optimistic target instead; the move's own response then writes
          // that same value and the registry entry clears on settle. A read
          // policy (not merge) because Apollo merge functions cannot identify
          // the enclosing entity.
          read(existing: string | null | undefined, { readField }: FieldFunctionOptions): string | null | undefined {
            const identifier = readField<string>('identifier');
            if (!identifier) return existing;
            const pending = getPendingMovedGroupId(identifier);
            return pending === undefined ? existing : pending;
          },
        },
      },
    },
    CadLayer: {
      fields: {
        visibleState: {
          merge(_, incoming = []) {
            return [...incoming];
          },
        },
      },
    },
    Organisation: {
      fields: {
        subscription: {
          merge: true,
        },
        credits: {
          merge: true,
        },
        odoo: {
          merge: true,
        },
        // No id: the user query and the organisation / admin queries each select other referral fields.
        referral: {
          merge: true,
        },
      },
    },
    // WallSketch data types should not be normalized - they are embedded objects
    SkeletonNodeData: {
      keyFields: false,
    },
    SkeletonEdgeData: {
      keyFields: false,
    },
  },

  dataIdFromObject: (object) => defaultDataIdFromObject({ ...object, id: object.id || object.identifier, _id: object.id || object.identifier }),
});
